import { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorBanner,
  PageHeader,
  PageLoading,
} from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { fabricSyncApi } from '@/services/fabric-sync-api';
import { PageContainer } from '@/components/layout';
import {
  DependencyGateDetailsCard,
  formatDate,
  formatScopeDescriptor,
  SyncSummaryCard,
} from './fabric-sync-status-page-shared';

export function TblCuratedSyncStatusPage() {
  const { data, loading, error } = useApi(() => fabricSyncApi.getCuratedSyncStatus(), []);

  const dependencies = useMemo(() => data?.dependencyGate.dependencies ?? [], [data]);

  if (loading && !data) {
    return <PageLoading />;
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Tbl - sync_status_curated"
        subtitle="Curated-layer sync observability and dependency gate readiness."
      />

      <ErrorBanner message={error} />

      {data && (
        <SyncSummaryCard
          title="Curated Sync Summary"
          sourceLayer={data.sourceLayer}
          asOf={data.asOf}
          dependencyGateStatus={data.dependencyGate.status}
          reasonCodes={data.reasonCodes}
        />
      )}

      {data && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Curated Run Envelope</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 pt-0 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Run Type</p>
              <p className="text-sm font-medium">{data.run.runType}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Run Status</p>
              <p className="text-sm font-medium">{data.run.runStatus}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Scope</p>
              <p className="text-sm font-medium">{data.run.scope}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Run ID</p>
              <p className="text-sm font-medium">{data.run.runId ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Started At</p>
              <p className="text-sm font-medium">{formatDate(data.run.startedAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Finished At</p>
              <p className="text-sm font-medium">{formatDate(data.run.finishedAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">As Of</p>
              <p className="text-sm font-medium">{formatDate(data.run.asOf)}</p>
            </div>
            <div className="sm:col-span-2 lg:col-span-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Scope Descriptor</p>
              <p className="text-sm font-medium break-all">{formatScopeDescriptor(data.run.scopeDescriptor)}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <DependencyGateDetailsCard dependencies={dependencies} emptyMessage="No curated dependencies found." />
    </PageContainer>
  );
}
